<template>
  <div class="managed-clients">
    <v-row>
      <v-col>
        <breadcrumb :items="breadcrumbs" />
      </v-col>
      <v-col class="text-right">
        <v-btn
          color="primary"
          class="mb-5"
          @click="showAddClientDialog = true"
        >
          <v-icon
            left
            size="22"
          >
            {{ icons.mdiAccountMultiple }}
          </v-icon>
          Add Client
        </v-btn>
      </v-col>
    </v-row>
    <v-card>
      <v-card-title>
        <v-row
          justify="center"
          class="mb-0"
        >
          <v-col class="col-md-6 text-center">
            <search-input
              v-model="search"
              :filters="searchFilters"
              :active-filter="activeSearchFilter"
              @filter-change="onSearchFilterChange"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="clients"
        :loading="isLoading"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        hide-default-footer
      >
        <template v-slot:progress>
          <v-progress-linear
            indeterminate
            class="mt-2"
            rounded
          ></v-progress-linear>
          <p class="text-center mt-1">
            Loading...Please wait
          </p>
        </template>
        <template v-slot:body="{ items }">
          <tbody v-if="clients.length > 0">
            <tr
              v-for="client in items"
              :key="client.id"
              class="clickable"
              @click="viewClient(client.id)"
            >
              <td>{{ client.name }}</td>
              <td>{{ client.account_number }}</td>
              <td>{{ client.backend_url }}</td>
              <td
                @click.stop
              >
                <v-tooltip
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="editClient(client)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiPencil }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="showDeleteClientDialog(client.id)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiDelete }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-show="!isLoading">
              <td
                :colspan="headers.length"
                style="text-align: center"
              >
                No clients found
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
      <v-divider />
      <v-pagination
        v-model="pagination.page"
        class="mt-1"
        :length="pagination.pageCount"
        :total-visible="8"
        @input="onPageChange"
      ></v-pagination>
    </v-card>
    <add-client-dialog
      :show-dialog="showAddClientDialog"
      :client="client"
      @close="
        showAddClientDialog = false
        client = {}
      "
      @client-added="getClients()"
    />
    <confirm-dialog
      :show-dialog="showConfirmDeleteDialog"
      :is-agree-button-loading="isConfirmDeleteDialogButtonLoading"
      :agree-text="'Delete'"
      @cancel="showConfirmDeleteDialog = false"
      @agree="deleteClient()"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions } from 'vuex'
import _ from 'lodash'
import {
  mdiMagnify, mdiPencil, mdiDelete, mdiAccountMultiple,
} from '@mdi/js'
import hasPermission from '@/mixins/hasPermission'
import AddClientDialog from '@/components/dialogs/AddManagedClientDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import BreadCrumb from '@/components/partials/BreadCrumb.vue'
import SearchInput from '@/components/partials/SearchInput.vue'

export default {
  components: {
    AddClientDialog,
    ConfirmDialog,
    breadcrumb: BreadCrumb,
    SearchInput,
  },
  mixins: [hasPermission],
  data() {
    return {
      showAddClientDialog: false,
      showConfirmDeleteDialog: false,
      isConfirmDeleteDialogButtonLoading: false,
      toDelete: null,
      sortBy: ['created_at'],
      sortDesc: [true],
      search: '',
      pagination: {
        page: 1,
        pageCount: 1,
        itemsPerPage: 10,
      },
      headers: [
        {
          text: 'Name',
          align: 'name',
          sortable: false,
        },
        {
          text: 'Account Number',
          align: 'account_number',
          sortable: false,
        },
        {
          text: 'Backend URL',
          align: 'backend_url',
          sortable: false,
        },
        { text: 'Actions', align: 'actions', sortable: false },
      ],
      activeSearchFilter: 'name',
      searchFilters: [
        {
          text: 'Name',
          value: 'name',
        },
        {
          text: 'Account Number',
          value: 'account_number',
        },
        {
          text: 'Backend URL',
          value: 'backend_url',
        },
      ],
      icons: {
        mdiMagnify,
        mdiPencil,
        mdiDelete,
        mdiAccountMultiple,
      },
      client: {},
      clients: [],
      isLoading: true,
      clientsUrl: 'managed-clients',
      breadcrumbs: [
        {
          text: 'Dashboard',
          disabled: false,
          to: { name: 'dashboard' },
        },
        {
          text: 'Managed Clients',
          disabled: false,
          to: { name: 'managed-clients' },
        },
      ],
    }
  },
  computed: {
    sortOrder() {
      return this.sortDesc[0] ? 'desc' : 'asc'
    },
    refreshTable() {
      return `${this.clientsUrl}|${this.search}|${this.sortBy}|${this.sortDesc}`
    },
  },
  watch: {
    refreshTable() {
      this.getClients()
    },
    activeSearchFilter() {
      if (this.search !== '') {
        this.getClients()
      }
    },
  },
  mounted() {
    if (!(this.can('managed-clients-edit') && this.can('managed-clients-delete'))) {
      this.removeActionsHeader()
    }
    this.getClients()
  },
  methods: {
    ...mapActions(['refreshClients']),
    getClients: _.debounce(function () {
      this.isLoading = true
      axios
        .get(
          `${this.clientsUrl}?paginate=true&sortBy=${this.sortBy[0]}&sortOrder=${this.sortOrder}&search=${this.search}&search_filter=${this.activeSearchFilter}&page=${this.pagination.page}`,
        )
        .then(response => {
          this.clients = response.data.data
          this.pagination.page = response.data.current_page
          this.pagination.pageCount = response.data.last_page
          this.pagination.itemsPerPage = response.data.per_page
          this.isLoading = false
        })
        .catch(error => {
          this.isLoading = false
          this.$notification.error(error.response.data.message)
        })
    }, 500),
    editClient(client) {
      this.client = client
      this.showAddClientDialog = true
    },
    showDeleteClientDialog(id) {
      this.showConfirmDeleteDialog = true
      this.toDelete = id
    },
    deleteClient() {
      this.isConfirmDeleteDialogButtonLoading = true
      axios
        .delete(`managed-clients/${this.toDelete}`)
        .then(() => {
          this.$notification.success('Client deleted successfully')
          this.isConfirmDeleteDialogButtonLoading = false
          this.showConfirmDeleteDialog = false
          this.getClients()
          this.refreshClients(true)
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
          this.isConfirmDeleteDialogButtonLoading = false
          this.showConfirmDeleteDialog = false
        })
    },
    onPageChange() {
      this.getClients()
    },
    onSearchFilterChange(filter) {
      this.activeSearchFilter = filter
    },
    viewClient(clientId) {
      this.$router.push({ name: 'managed-client-details', params: { id: clientId } })
    },
    removeActionsHeader() {
      this.headers = this.headers.filter(header => header.value !== 'actions')
    },
  },
}
</script>

<style scoped>
.clickable {
  cursor: pointer;
}
</style>
